import { Toast as ToastPrimitive } from 'radix-ui';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type LiHTMLAttributes,
  type OlHTMLAttributes,
  type ReactNode,
} from 'react';

import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TOAST — WCAG 4.1.3 (Status Messages) AND THE TIMER THAT USUALLY BREAKS IT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Follows `Button.tsx`'s five rules. Only what is specific to a toast is argued here.
 *
 * ── ONE LIVE REGION, AND IT IS NOT OURS ────────────────────────────────────
 * SC 4.1.3 asks that a status message be programmatically determinable "without receiving
 * focus". Radix already does that: `Toast.Root` renders a visually-hidden `role="status"`
 * announcer into `document.body` containing the toast's text, computed by walking the toast's
 * own DOM. Nothing in this file adds `role="status"`, `role="alert"` or `aria-live` — a second
 * live region wrapping the first is the single most common toast defect, and it does not
 * announce twice as well: screen readers deduplicate unpredictably, and NVDA in particular
 * will read one, the other, or a spliced mixture depending on timing.
 *
 * What this file does own is WHAT that announcer says, because Radix builds it from the visible
 * markup. Two consequences drive the layout below:
 *
 *   · An `aria-hidden` node is skipped, so the variant icon contributes nothing — which is why
 *     the variant word exists as a separate `sr-only` span. Delete it and a screen-reader user
 *     cannot tell an error from a confirmation.
 *   · The collected strings are rendered as ADJACENT TEXT NODES with no separator, so a title
 *     and a description butt together as "…extension 201The PBX rejected…". The `sr-only`
 *     punctuation spans between the blocks are that separator. They are siblings of `Title`,
 *     `Description` and `Action` rather than children of any of them, so each element keeps a
 *     clean `textContent` — which for the action button is also its accessible name.
 *     Note the action needs one too: Radix substitutes its `altText` into the same flat list,
 *     so a title with no trailing punctuation runs into it as "…extension 201Retry from…".
 *
 * ── THE DURATION IS PER-VARIANT, AND A DANGER TOAST NEVER EXPIRES ──────────
 * SC 2.2.1 (Timing Adjustable) treats an auto-dismiss as a time limit set by the content.
 * Radix pauses the timer on hover and on focus, which is a real "extend" mechanism for a
 * pointer or keyboard user — and no help at all to the population that most needs it, because
 * a screen-reader user reading with a virtual cursor never hovers and never focuses. Their
 * clock started when the announcement began and it cannot be stopped.
 *
 * So the timer is sized to the cost of missing the message:
 *
 *   · `info` / `success` — 6 s. Nothing is lost by missing "Extension saved"; the state it
 *     reports is visible on the page. 5 s (Radix's default) is roughly fifteen words at a
 *     typical synthetic-speech rate, and a title plus a description routinely exceeds that.
 *   · `warning` — 12 s. Something needs reading and possibly acting on, but the page is still
 *     in a usable state.
 *   · `danger` — `Infinity`. An error toast is frequently the ONLY record that the operation
 *     failed and the only place the reason appears; a timer that deletes it is a timer that
 *     deletes the evidence. It is dismissed explicitly, by the close button, by Escape, or by
 *     a swipe. This is also the honest reading of 2.2.1: rather than build an extend-the-timer
 *     affordance nobody would find, do not start a timer.
 *
 * A caller can override with `duration`, including `Infinity`. Note that Radix resolves
 * `duration || providerDuration`, so a caller passing `0` to mean "never" would silently get
 * the 5 s default — the exact opposite. `normaliseDuration` folds 0, NaN and negatives to
 * `Infinity` before Radix ever sees them.
 *
 * ── COLOUR IS NEVER THE ONLY SIGNAL (SC 1.4.1) ─────────────────────────────
 * Three independent channels carry the variant, and each is enough on its own:
 *   1. an icon whose SHAPE differs (circle-i, circle-check, triangle-bang, circle-cross), so
 *      the toast survives greyscale and every form of colour-vision deficiency;
 *   2. an `sr-only` word — "Error", "Success" — which is what the announcer reads;
 *   3. the title, which callers are expected to write as a sentence that says what happened.
 * The variant colour is the fourth channel and the only decorative one.
 *
 * ── WHY THE PANEL IS `surface-raised` AND NOT A TINT ───────────────────────
 * The obvious design is `bg-danger-subtle` with `text-danger`. It was rejected on evidence:
 * `tokens.contrast.test.ts` proves `--pbx-danger` on `--pbx-danger-subtle` and `--pbx-fg*` on
 * the four SURFACE tokens, and nothing else. A toast needs body text as well as a heading, and
 * `--pbx-fg-muted` on `--pbx-danger-subtle` is a pair no test in this repo measures — an
 * unverified ratio in the component whose whole job is to be read in a hurry. The variant
 * colour is therefore confined to the icon and the leading border, both of which ARE proven
 * (status colours clear 4.5:1 on every surface, well past the 3:1 that 1.4.11 asks of a
 * non-text boundary).
 *
 * ── ⚠️ KNOWN GAP: A TOAST RAISED WHILE A MODAL IS OPEN ─────────────────────
 * Radix's `Dialog` calls `hideOthers()` from the `aria-hidden` package, which sets
 * `aria-hidden="true"` on every sibling of the dialog content in `document.body`. The toast
 * viewport is rendered in the app tree, so it goes under one of those siblings and is hidden;
 * `DismissableLayer.Branch` (which the viewport is) exempts it from pointer and focus
 * dismissal, NOT from `aria-hidden`.
 *
 * The ANNOUNCEMENT still gets through, and it is worth being precise about why, because the
 * plausible-sounding "the toast is silent over a modal" is wrong and would send someone
 * looking for the wrong fix. Two independent reasons: `hideOthers` runs once when the dialog
 * opens and installs no MutationObserver, so the announcer — portalled to `document.body`
 * only when the toast is raised, i.e. afterwards — is never marked; and `hideOthers` collects
 * `[aria-live]` nodes into its keep-set anyway, so it would skip the announcer even if the
 * order were reversed. Asserted in `Toast.test.tsx` so this stays true.
 *
 * What is genuinely lost is everything AFTER the announcement: the toast text, its action and
 * its close button are out of the accessibility tree for as long as the modal is open, and F8
 * lands on a hidden region. A `danger` toast survives this — it never expires, so it is still
 * there and reachable once the modal closes — but a `success` one will have gone. So: prefer
 * showing the failure inside the dialog itself. `announcerContainer` is exposed for other
 * reasons (a caller who portals the whole app somewhere unusual); it does NOT need to be set
 * for the modal case, because the announcer was never the broken half.
 */

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

/**
 * The policy from the header, as data so it can be asserted directly and read by a caller who
 * wants to know what they are opting out of.
 */
export const TOAST_DURATIONS: Readonly<Record<ToastVariant, number>> = {
  info: 6_000,
  success: 6_000,
  warning: 12_000,
  danger: Number.POSITIVE_INFINITY,
};

/**
 * `foreground` makes Radix's announcer `aria-live="assertive"`, which INTERRUPTS whatever the
 * screen reader is currently saying; `background` makes it `polite`, which queues.
 *
 * Interrupting is a cost paid by the user, so it is spent only where the alternative is worse:
 * a failure or a warning the user is about to act on wrongly. "Extension saved" waits its
 * turn — an assertive confirmation cuts off the sentence the user was reading to produce
 * information they could have had a moment later, which is how a well-meant live region
 * becomes the reason someone turns announcements off.
 */
const ANNOUNCEMENT_TYPE: Readonly<Record<ToastVariant, 'foreground' | 'background'>> = {
  info: 'background',
  success: 'background',
  warning: 'foreground',
  danger: 'foreground',
};

/** Read by the announcer. "Error" rather than "Danger" — it is what happened, not a palette. */
const VARIANT_WORD: Readonly<Record<ToastVariant, string>> = {
  info: 'Information',
  success: 'Success',
  warning: 'Warning',
  danger: 'Error',
};

/** Icon and leading border only — see the header for why the panel itself stays neutral. */
const VARIANT_ACCENT: Readonly<Record<ToastVariant, string>> = {
  info: 'border-l-info text-info',
  success: 'border-l-success text-success',
  warning: 'border-l-warning text-warning',
  danger: 'border-l-danger text-danger',
};

const svgProps = {
  viewBox: '0 0 20 20',
  width: 20,
  height: 20,
  fill: 'none',
  focusable: false,
  'aria-hidden': true,
} as const;

/**
 * Four distinct SILHOUETTES, not four colours of the same glyph. A ring, a tick, a triangle
 * and a cross remain four different things at 1-bit depth, which is the test 1.4.1 is really
 * asking about.
 */
const VARIANT_ICON: Readonly<Record<ToastVariant, ReactNode>> = {
  info: (
    <svg {...svgProps}>
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="10" cy="6.25" r="1" fill="currentColor" />
      <path d="M10 9.25v4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  success: (
    <svg {...svgProps}>
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="m6.25 10.25 2.5 2.5 5-5.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  warning: (
    <svg {...svgProps}>
      <path
        d="M10 2.75 18.25 17H1.75z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M10 7.75v3.75" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="10" cy="14.25" r="1" fill="currentColor" />
    </svg>
  ),
  danger: (
    <svg {...svgProps}>
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="m7.25 7.25 5.5 5.5m0-5.5-5.5 5.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const CloseIcon = () => (
  <svg {...svgProps} width={16} height={16}>
    <path
      d="m5.5 5.5 9 9m0-9-9 9"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * An optional button on the toast — "Retry", "Undo".
 *
 * `altText` is not optional and Radix will refuse to render without it, correctly: the button
 * is excluded from the announcement (a screen-reader user hears the toast text, not its
 * controls) and `altText` is what is read in its place. It should describe how to reach the
 * same outcome by another route, because a toast is transient and the button may well be gone
 * by the time the user goes looking for it — "Undo from the call log" beats "Undo".
 */
export interface ToastActionSpec {
  label: string;
  altText: string;
  onClick: () => void;
}

/**
 * `| undefined` on every optional member, deliberately. Under `exactOptionalPropertyTypes` a
 * bare `description?: string` REJECTS `description: maybeUndefined`, and this is an options
 * bag built at a call site from values that are frequently absent — `toast({ title, description:
 * error.detail })` is the normal shape and must not need a conditional spread to type-check.
 */
export interface ToastOptions {
  title: string;
  description?: string | undefined;
  /** Defaults to `info`. See the header: this selects the icon, the word, and the timer. */
  variant?: ToastVariant | undefined;
  /** Milliseconds, or `Infinity` to require an explicit dismissal. Overrides `TOAST_DURATIONS`. */
  duration?: number | undefined;
  action?: ToastActionSpec | undefined;
  /** Overrides the default `Dismiss: <title>` name on the close button. */
  closeLabel?: string | undefined;
}

export interface ToastApi {
  /** Shows a toast and returns its id, so a long-lived one can be dismissed programmatically. */
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  /** For a route change: a toast about the page you just left is noise at best. */
  dismissAll: () => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Throws rather than no-oping when there is no provider above it.
 *
 * A silent fallback would mean the "Could not reach the switch" toast never appears and
 * nothing anywhere says why — a failure that is invisible in exactly the situation where the
 * user most needs to be told something. Loud, at the first call, in development.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) {
    throw new Error(
      'useToast() was called outside <ToastProvider>. Wrap the app (or the route) in ' +
        '<ToastProvider> — without it a toast would be dropped silently.',
    );
  }
  return api;
}

interface ToastRecord {
  id: string;
  title: string;
  description: string | undefined;
  variant: ToastVariant;
  duration: number;
  action: ToastActionSpec | undefined;
  closeLabel: string | undefined;
}

/** See the header: Radix's `duration || providerDuration` would turn a caller's 0 into 5 s. */
function normaliseDuration(ms: number): number {
  return Number.isFinite(ms) && ms > 0 ? ms : Number.POSITIVE_INFINITY;
}

/**
 * Enforces `limit`, and it does NOT simply drop the oldest.
 *
 * A burst of failures is the normal way this queue overflows — twelve extensions fail to
 * provision and twelve toasts arrive in a second. Dropping oldest-first would evict the errors
 * (which never expire and are the only record of what went wrong) in favour of whatever
 * "Saved" landed most recently. So the auto-dismissing toasts are given up first, in age
 * order, and only if that is not enough does an indefinite one go — a stack that grows without
 * bound is its own accessibility failure, because it pushes the viewport past the bottom of a
 * zoomed window.
 */
function evict(list: readonly ToastRecord[], limit: number): ToastRecord[] {
  if (limit <= 0 || list.length <= limit) return [...list];
  const surplus = list.length - limit;
  const doomed = new Set<string>();
  for (const item of list) {
    if (doomed.size >= surplus) break;
    if (Number.isFinite(item.duration)) doomed.add(item.id);
  }
  for (const item of list) {
    if (doomed.size >= surplus) break;
    doomed.add(item.id);
  }
  return list.filter((item) => !doomed.has(item.id));
}

export interface ToastProviderProps extends Omit<OlHTMLAttributes<HTMLOListElement>, 'children'> {
  /** The app. Toasts are portalled into the viewport, so this is not where they render. */
  children?: ReactNode;
  /** Most toasts on screen at once. `0` or less means unbounded. See `evict`. */
  limit?: number;
  /** Prefixed to every announcement ("Notification: Extension saved"). Localise it. */
  label?: string;
  /**
   * Names the viewport landmark. `{hotkey}` is substituted by Radix, and leaving it in is the
   * point: the region's own name is how a screen-reader user learns the shortcut exists.
   */
  viewportLabel?: string;
  /**
   * Moves focus to the viewport. F8 by default, and it is a FUNCTION key on purpose — SC 2.1.4
   * (Character Key Shortcuts) applies to single letters, digits and punctuation, which a speech-
   * input user triggers by talking. F8 is outside that criterion and needs no toggle.
   */
  hotkey?: string[];
  swipeDirection?: 'up' | 'down' | 'left' | 'right';
  /**
   * Where Radix portals its live region. Defaults to `document.body`, which is right almost
   * always — see the ⚠️ note in the header for why an open `Modal` is NOT a reason to change it.
   */
  announcerContainer?: Element | DocumentFragment;
}

/**
 * `forwardRef` goes to the viewport `<ol>` — the only DOM this component owns, and the node a
 * caller would need in order to measure it or scroll it.
 */
export const ToastProvider = forwardRef<HTMLOListElement, ToastProviderProps>(
  function ToastProvider(
    {
      children,
      limit = 4,
      label = 'Notification',
      viewportLabel = 'Notifications ({hotkey})',
      hotkey,
      swipeDirection = 'right',
      announcerContainer,
      className,
      ...rest
    },
    ref,
  ) {
    const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
    // Not `useState`: an id is not rendered state, and bumping a counter through `setState`
    // would make two toasts raised in the same handler collide on the pre-render value.
    const counter = useRef(0);

    const dismiss = useCallback((id: string) => {
      setToasts((current) => current.filter((item) => item.id !== id));
    }, []);

    const dismissAll = useCallback(() => {
      setToasts([]);
    }, []);

    const toast = useCallback(
      (options: ToastOptions): string => {
        const variant = options.variant ?? 'info';
        counter.current += 1;
        const id = `pbx-toast-${String(counter.current)}`;
        const record: ToastRecord = {
          id,
          title: options.title,
          description: options.description,
          variant,
          duration: normaliseDuration(options.duration ?? TOAST_DURATIONS[variant]),
          action: options.action,
          closeLabel: options.closeLabel,
        };
        setToasts((current) => evict([...current, record], limit));
        return id;
      },
      [limit],
    );

    // Stable so `toast` can sit in a `useEffect` dependency array without re-firing it — the
    // failure mode there is an effect that raises a toast on every render.
    const api = useMemo<ToastApi>(() => ({ toast, dismiss, dismissAll }), [toast, dismiss, dismissAll]);

    return (
      <ToastContext.Provider value={api}>
        <ToastPrimitive.Provider
          label={label}
          swipeDirection={swipeDirection}
          {...(hotkey === undefined ? {} : { hotkey })}
          {...(announcerContainer === undefined ? {} : { announcerContainer })}
        >
          {children}
          {toasts.map((item) => (
            <ToastItem
              key={item.id}
              title={item.title}
              description={item.description}
              variant={item.variant}
              duration={item.duration}
              action={item.action}
              closeLabel={item.closeLabel}
              /**
               * Fires for the timer, the close button, Escape and a swipe alike, which is why
               * removal is wired here and not to the close button. Radix moves focus to the
               * viewport before this runs when focus was inside the toast, so unmounting does
               * not drop a keyboard user onto `<body>`.
               */
              onOpenChange={(open) => {
                if (!open) dismiss(item.id);
              }}
            />
          ))}
          <ToastPrimitive.Viewport
            ref={ref}
            label={viewportLabel}
            data-pbx-toast-viewport
            className={cn(
              // `fixed` bottom-right, and full-width below the breakpoint: a fixed 420px panel
              // on a 320px viewport is a horizontal scrollbar, which 1.4.10 (Reflow) forbids.
              //
              // ── NEVER OVER THE CALL BAR, A DIALOG'S ACTIONS, OR THE HOME INDICATOR ──
              // `bottom` is the larger of: the docked call bar's measured height
              // (`SoftphonePanel` publishes `--pbx-call-bar-height` while a call is up) plus the
              // safe-area inset; and the top-most dialog's foot (`DialogFoot` publishes
              // `--pbx-dialog-foot` while one is open). A toast that lands on Hang up, or on a
              // drawer's Save, is a toast that must be dismissed before the thing under it can
              // be pressed — the two surfaces a notification may never cover.
              //
              // `pointer-events-auto`: a modal dialog turns the body's pointer events off, and
              // this viewport is in the body. Without this a toast shown while a drawer is open
              // could be neither hovered (to pause it) nor dismissed. `DialogFrame` treats a
              // press on this viewport as not-outside, so it does not close the dialog either.
              'pointer-events-auto fixed right-0 z-toast',
              'bottom-[max(calc(var(--pbx-call-bar-height,0px)+env(safe-area-inset-bottom,0px)),var(--pbx-dialog-foot,0px))]',
              'm-0 flex w-full max-w-[26rem] list-none flex-col gap-2 p-4',
              className,
            )}
            {...rest}
          />
        </ToastPrimitive.Provider>
      </ToastContext.Provider>
    );
  },
);

/**
 * `onPause`/`onResume` are dropped from the native props because they collide: React declares
 * them as MEDIA events (`ReactEventHandler`), Radix redefines them on a toast as "the
 * auto-dismiss timer was paused/resumed" (`() => void`). Keeping React's would silently hand a
 * caller a handler that Radix calls with no argument, so the narrower Radix meaning wins and
 * the media ones — which an `<li>` has no use for — go.
 */
export interface ToastItemProps
  extends Omit<LiHTMLAttributes<HTMLLIElement>, 'title' | 'children' | 'onPause' | 'onResume'> {
  /**
   * Shadows the HTML `title` attribute, and that is a feature. A native `title` tooltip is
   * unreachable by touch, unreadable at a text-zoom setting and announced inconsistently; there
   * is no version of this component that wants one.
   */
  title: string;
  description?: string | undefined;
  variant?: ToastVariant | undefined;
  duration?: number | undefined;
  action?: ToastActionSpec | undefined;
  closeLabel?: string | undefined;
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
}

/**
 * One toast. Rendered by `ToastProvider` from the queue, and exported for the rare caller that
 * wants a declaratively-controlled toast (a persistent "You are offline" banner, say) rather
 * than an imperative one.
 */
export const ToastItem = forwardRef<HTMLLIElement, ToastItemProps>(function ToastItem(
  {
    title,
    description,
    variant = 'info',
    duration,
    action,
    closeLabel,
    open,
    defaultOpen,
    onOpenChange,
    className,
    ...rest
  },
  ref,
) {
  return (
    <ToastPrimitive.Root
      ref={ref}
      duration={normaliseDuration(duration ?? TOAST_DURATIONS[variant])}
      type={ANNOUNCEMENT_TYPE[variant]}
      {...(open === undefined ? {} : { open })}
      {...(defaultOpen === undefined ? {} : { defaultOpen })}
      {...(onOpenChange === undefined ? {} : { onOpenChange })}
      className={cn(
        'flex w-full items-start gap-3 rounded-lg border border-l-4 border-border',
        'bg-surface-raised p-4 text-fg shadow-lg',
        VARIANT_ACCENT[variant],
        className,
      )}
      {...rest}
    >
      {/**
       * The variant, for the announcer and for anyone not looking at the screen. Its trailing
       * ": " is load-bearing — see the header: Radix concatenates the collected text nodes with
       * no separator of its own.
       */}
      <span className="sr-only">{`${VARIANT_WORD[variant]}: `}</span>

      {/* Decoration, and `aria-hidden` is also what keeps it out of the announcement. */}
      <span aria-hidden="true" className="mt-px shrink-0">
        {VARIANT_ICON[variant]}
      </span>

      <div className="min-w-0 flex-1">
        {/* `font-semibold`, not a heading: a toast is not a section of the document, and an
            <h*> here would appear in the heading list of a page it does not belong to. */}
        <ToastPrimitive.Title className="text-sm font-semibold text-fg">{title}</ToastPrimitive.Title>
        {description === undefined ? null : (
          <>
            <span className="sr-only">{'. '}</span>
            <ToastPrimitive.Description className="mt-1 text-sm text-fg-muted">
              {description}
            </ToastPrimitive.Description>
          </>
        )}
      </div>

      {action === undefined ? null : (
        <>
          {/**
           * The same separator problem as title/description, one element further along: Radix
           * appends the action's `altText` to the collected strings with nothing between them,
           * so a title with no trailing punctuation runs straight into it — "…extension
           * 201Retry from the extension list". Sibling of the Action, not a child, so the
           * button's own `textContent` (and therefore its accessible name) stays "Retry".
           */}
          <span className="sr-only">{'. '}</span>
          <ToastPrimitive.Action
            altText={action.altText}
            onClick={action.onClick}
            className={cn(
              'inline-flex min-h-8 shrink-0 items-center justify-center rounded-md border',
              'border-border-strong bg-surface px-3 text-sm font-medium text-fg',
              'transition-colors hover:bg-surface-sunken',
            )}
          >
            {action.label}
          </ToastPrimitive.Action>
        </>
      )}

      {/**
       * The name includes the title. Several toasts stack, and a screen reader's element list
       * would otherwise show four buttons all called "Dismiss" with nothing to choose between
       * them — SC 2.4.6 wants a label that describes its purpose, and here the purpose is
       * "dismiss THIS one".
       */}
      <ToastPrimitive.Close
        aria-label={closeLabel ?? `Dismiss: ${title}`}
        className={cn(
          'inline-flex min-h-8 min-w-8 shrink-0 items-center justify-center rounded-md',
          'text-fg-muted transition-colors hover:bg-surface-sunken hover:text-fg',
        )}
      >
        <CloseIcon />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
});
